import { ErrorHandler, Injectable, inject } from '@angular/core';
import { ToastService } from './toast.service';
import { LoggingService } from './logging.service';
import { environment } from '../../environments/environment';

/**
 * Global error handler for the application
 * Catches unhandled errors and provides user-friendly feedback
 */
@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  private toastService = inject(ToastService);
  private loggingService = inject(LoggingService);

  // Track recently shown errors to prevent duplicate toasts
  private recentErrors = new Set<string>();
  private readonly ERROR_THROTTLE_MS = 5000;

  handleError(error: any): void {
    // Log the error
    this.logError(error);

    // Show user-friendly message (throttled to prevent spam)
    this.showUserFriendlyError(error);

    // In development, also log to console for debugging
    if (!environment.production) {
      console.error('Global error handler caught:', error);
    }
  }

  /**
   * Log the error for debugging/monitoring
   */
  private logError(error: any): void {
    const errorMessage = this.extractErrorMessage(error);
    const errorStack = error?.stack || '';
    const timestamp = new Date().toISOString();

    // Log to console in development
    this.loggingService.log(`[${timestamp}] ERROR: ${errorMessage}`);
    if (errorStack) {
      this.loggingService.log(`Stack: ${errorStack}`);
    }

    // In production, you could send to a monitoring service here
    // Example: Sentry, Rollbar, etc.
    if (environment.production) {
      // TODO: Send to monitoring service
      // this.monitoringService.logError(errorMessage, errorStack, timestamp);
    }
  }

  /**
   * Show a user-friendly error message
   */
  private showUserFriendlyError(error: any): void {
    const errorMessage = this.extractErrorMessage(error);
    const errorKey = this.generateErrorKey(errorMessage);

    // Throttle duplicate errors
    if (this.recentErrors.has(errorKey)) {
      return;
    }

    this.recentErrors.add(errorKey);
    setTimeout(() => {
      this.recentErrors.delete(errorKey);
    }, this.ERROR_THROTTLE_MS);

    // Determine error type and show appropriate message
    if (this.isNetworkError(error)) {
      this.toastService.networkError(false);
    } else if (this.isChunkLoadError(error)) {
      this.toastService.error(
        'Fehler beim Laden der Anwendung. Bitte laden Sie die Seite neu. / Error loading application. Please refresh the page.',
        8000
      );
    } else if (this.isHttpError(error)) {
      const httpMessage = this.getHttpErrorMessage(error);
      this.toastService.apiError(httpMessage);
    } else {
      // Generic error message
      this.toastService.error('Ein unerwarteter Fehler ist aufgetreten. / An unexpected error occurred.');
    }
  }

  /**
   * Extract a readable error message from various error types
   */
  private extractErrorMessage(error: any): string {
    if (typeof error === 'string') {
      return error;
    }

    if (error?.message) {
      return error.message;
    }

    if (error?.error?.message) {
      return error.error.message;
    }

    if (error?.rejection?.message) {
      return error.rejection.message;
    }

    return 'Unknown error';
  }

  /**
   * Generate a key for error throttling
   */
  private generateErrorKey(message: string): string {
    // Use first 100 chars of message as key
    return message.substring(0, 100);
  }

  /**
   * Check if error is a network error
   */
  private isNetworkError(error: any): boolean {
    return (
      error?.message?.includes('NetworkError') ||
      error?.message?.includes('Failed to fetch') ||
      error?.message?.includes('Network request failed') ||
      error?.status === 0 ||
      error?.name === 'NetworkError'
    );
  }

  /**
   * Check if error is a chunk loading error (lazy-loaded modules)
   */
  private isChunkLoadError(error: any): boolean {
    const message = this.extractErrorMessage(error);
    return (
      message.includes('ChunkLoadError') ||
      message.includes('Loading chunk') ||
      message.includes('Failed to load module')
    );
  }

  /**
   * Check if error is an HTTP error
   */
  private isHttpError(error: any): boolean {
    return error?.status !== undefined && error?.status >= 400;
  }

  /**
   * Get user-friendly message for HTTP errors
   */
  private getHttpErrorMessage(error: any): string {
    const status = error?.status;
    const serverMessage = error?.error?.error || error?.error?.message;

    // Use server message if available and readable
    if (serverMessage && typeof serverMessage === 'string' && serverMessage.length < 100) {
      return `${serverMessage} (${status})`;
    }

    // Fall back to status-based messages
    switch (status) {
      case 400:
        return 'Ungültige Anfrage. / Invalid request. (400)';
      case 401:
        return 'Nicht autorisiert. / Unauthorized. (401)';
      case 403:
        return 'Zugriff verweigert. / Access denied. (403)';
      case 404:
        return 'Ressource nicht gefunden. / Resource not found. (404)';
      case 429:
        return 'Zu viele Anfragen. Bitte warten Sie einen Moment. / Too many requests. Please wait. (429)';
      case 500:
        return 'Serverfehler. Bitte versuchen Sie es später erneut. / Server error. Please try again later. (500)';
      case 503:
        return 'Service nicht verfügbar. / Service unavailable. (503)';
      default:
        if (status >= 500) {
          return `Serverfehler (${status}). Bitte versuchen Sie es später erneut. / Server error (${status}). Please try again later.`;
        } else if (status >= 400) {
          return `Anfragefehler (${status}). / Request error (${status}).`;
        }
        return `HTTP-Fehler: ${status} / HTTP error: ${status}`;
    }
  }
}
